import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { OperationTypeLiabilities } from "../../model/domain/Entity";
import { LiabilitiesModel } from "./../../model/domain/LiabilitiesModel";
import { FilterDateType } from "./../../model/domain/MonitorFilter";
import { ITicketableService, StatusDetails } from "./ITicketableService";

@Injectable({
  providedIn: "root",
})
export class LiabilitiesService
  implements ITicketableService<LiabilitiesModel>
{
  private readonly name = "liabilities";

  constructor(private http: HttpClient) {}

  registerTicket(ticket: LiabilitiesModel) {
    return this.http.post(
      `${environment.apiUrls.tickets}/${this.name}/`,
      ticket
    );
  }

  updateTicket(id: number, ticket: LiabilitiesModel) {
    return this.http.put(
      `${environment.apiUrls.tickets}/${this.name}/${id}`,
      ticket
    );
  }

  updateSettlementDate(id: number, settlementDate: Date) {
    return this.http.put(
      `${environment.apiUrls.tickets}/${this.name}/update-settlementDate/${id}`,
      settlementDate
    );
  }

  getTicket(id: number) {
    return this.http.get<LiabilitiesModel>(
      `${environment.apiUrls.tickets}/${this.name}/${id}`
    );
  }

  getSovereignStatus() {
    return this.http.get<boolean>(
      `${environment.apiUrls.tickets}/${this.name}/sovereign-status`
    );
  }

  listTicket<T>(
    dateType: FilterDateType = null,
    startDate: Date = null,
    endDate: Date = null,
    statusId: number = null,
    operationTypeId: number = null,
    settlementTypeId: number = null,
    portfolioCode: string = null,
    portfolioName: string = null,
    portfolioDocument: string = null,
    ticketId: number = null,
    accountManagerId: number = null,
    page: number = null,
    pageSize: number = null,
    isOutstanding: boolean = false
  ): Observable<T> {
    const params: any = { isOutstanding: isOutstanding };

    const listMethod = isOutstanding
      ? "ordered-by-settlement-date"
      : "list-paginated";

    if (dateType) {
      params.dateType = dateType;
    }

    if (startDate) {
      params.startDate = startDate.toJSON();
    }

    if (endDate) {
      params.endDate = endDate.toJSON();
    }

    if (statusId) {
      params.statusId = statusId.toString();
    }

    if (operationTypeId) {
      params.operationTypeId = operationTypeId;
    }

    if (settlementTypeId) {
      params.settlementTypeId = settlementTypeId;
    }

    if (portfolioCode) {
      params.portfolioCode = portfolioCode;
    }

    if (portfolioName) {
      params.portfolioName = portfolioName;
    }

    if (portfolioDocument) {
      params.portfolioDocument = portfolioDocument;
    }

    if (ticketId) {
      params.ticketId = ticketId;
    }

    if (accountManagerId) {
      params.accountManagerId = accountManagerId;
    }

    if (page) {
      params.page = page;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    return this.http.get<T>(
      `${environment.apiUrls.tickets}/${this.name}/${listMethod}`,
      {
        params,
      }
    );
  }

  forwardTicket(id: number) {
    return this.http.post<StatusDetails>(
      `${environment.apiUrls.tickets}/${this.name}/${id}/forward`,
      {}
    );
  }

  cancelDraftTicket(id: number): Observable<StatusDetails> {
    return this.http.post<StatusDetails>(
      `${environment.apiUrls.tickets}/${this.name}/${id}/cancel`,
      {}
    );
  }

  getQuotationDate(
    fundDocument: string = null,
    operationTypeId: OperationTypeLiabilities
  ) {
    const params: any = {};

    if (fundDocument) {
      params.fundDocument = fundDocument;
    }

    if (operationTypeId) {
      params.operationTypeId = operationTypeId;
    }

    return this.http.get<Date>(
      `${environment.apiUrls.tickets}/${this.name}/quotation-date`,
      { params }
    );
  }

  getSettlementDate(
    fundDocument: string = null,
    operationTypeId: OperationTypeLiabilities,
    quotationDate: Date
  ) {
    const params: any = {};

    if (fundDocument) {
      params.fundDocument = fundDocument;
    }

    if (operationTypeId) {
      params.operationTypeId = operationTypeId;
    }

    if (quotationDate) {
      params.quotationDate = quotationDate.toISOString();
    }

    return this.http.get<Date>(
      `${environment.apiUrls.tickets}/${this.name}/settlement-date`,
      { params }
    );
  }

  getEletronicTransferQuotaTicket(id: number): Observable<any> {
    return this.http.get<LiabilitiesModel>(
      `${environment.apiUrls.tickets}/${this.name}/${id}/electronic-transfer`
    );
  }

  approveWithEletronicTransfer(
    ticketId: number,
    canSendTransferForLiquidation: boolean,
    comments: string = ""
  ) {
    return this.http
      .post(`${environment.apiUrls.tickets}/ticket/${ticketId}/approve`, {
        comments,
      })
      .pipe(
        map((xyz) => {
          if (canSendTransferForLiquidation) {
            return this.getEletronicTransferQuotaTicket(ticketId);
          } else {
            return of({
              approved: true,
              canSendTranfer: canSendTransferForLiquidation,
            });
          }
        })
      );
  }
}
